import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hub } from "aws-amplify/utils";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

// ─── SSO callback (FMP-AU-001, FMP-AU-002) ───────────────────────────────────
// The Hosted UI redirects here with ?code=… after the IdP round-trip. Amplify
// performs the PKCE code exchange itself as soon as the library initialises, so
// this page deliberately does NOT read the query string — it waits for the
// exchange to land, then hydrates app state.
//
// Contrast the DevSecOps Dashboard, which read raw tokens out of the URL query
// string into localStorage. Tokens must never travel in a URL: query strings
// leak into browser history, Referer headers and server access logs.

// The exchange is fast but not synchronous with mount. Poll briefly instead of
// relying on the Hub event alone — the event can fire before this component
// subscribes, which would otherwise hang on the spinner forever.
const POLL_INTERVAL_MS = 250;
const POLL_TIMEOUT_MS = 15000;

const NO_ROLE_MESSAGE =
  "Your account has no portal role assigned. Contact your administrator to request access.";
const SSO_INCOMPLETE_MESSAGE =
  "Single sign-on did not complete. Your identity provider rejected the " +
  "request, or the sign-in session expired. Please try again.";

// FMP-NV-001: when Cognito refuses a sign-in (e.g. the pre-token-generation
// trigger raising NoPortalRole) it redirects here with ?error=…&error_description=…
// instead of ?code=…. Neither parameter carries a token, so reading them does not
// break the "tokens never in a URL" rule above. The description is matched, never
// rendered: it is URL-controlled text.
export function oauthErrorMessage(search) {
  const params = new URLSearchParams(search || "");
  if (!params.get("error")) return null;
  const description = params.get("error_description") || "";
  return /no portal role/i.test(description) ? NO_ROLE_MESSAGE : SSO_INCOMPLETE_MESSAGE;
}

export default function SsoCallbackPage() {
  const navigate = useNavigate();
  const { refreshAuthState } = useAuth();
  const [error, setError] = useState(null);
  // Guards against the Hub event and the poll both completing the flow.
  const settledRef = useRef(false);

  useEffect(() => {
    let pollTimer = null;
    let timeoutTimer = null;

    const fail = (message) => {
      if (settledRef.current) return;
      settledRef.current = true;
      // Leave no half-established session behind: a federated identity that
      // failed the role check must not linger in Amplify's store, or the next
      // page load would treat it as a valid signed-in session.
      signOut().catch(() => {});
      setError(message);
    };

    // A refused sign-in is final: show why at once rather than polling for a
    // session that will never arrive and then reporting a misleading timeout.
    const refusal = oauthErrorMessage(window.location.search);
    if (refusal) {
      fail(refusal);
      return undefined;
    }

    const succeed = async () => {
      if (settledRef.current) return;
      settledRef.current = true;
      // Re-run AuthProvider.loadSession so user/role come from the new tokens.
      const session = await refreshAuthState();
      // FMP-AU-002: role comes from the cognito:groups claim. A federated user
      // with no mapped group is authenticated but not authorised — refuse here
      // rather than dropping them into an empty portal. Server-side enforcement
      // is authoritative; this is the UX half.
      if (!session?.role) {
        settledRef.current = false;
        fail(
          "You signed in successfully, but your account has no portal role assigned. " +
            "Contact your administrator to request access."
        );
        return;
      }
      navigate("/", { replace: true });
    };

    const checkSession = async () => {
      try {
        const session = await fetchAuthSession();
        if (session?.tokens?.idToken) {
          await succeed();
          return true;
        }
      } catch {
        // Not ready yet — the poll will try again.
      }
      return false;
    };

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signInWithRedirect") {
        checkSession();
      }
      if (payload.event === "signInWithRedirect_failure") {
        fail(SSO_INCOMPLETE_MESSAGE);
      }
    });

    checkSession();
    pollTimer = setInterval(checkSession, POLL_INTERVAL_MS);
    timeoutTimer = setTimeout(() => {
      fail("Single sign-on timed out before a session was established. Please try again.");
    }, POLL_TIMEOUT_MS);

    return () => {
      unsubscribe();
      if (pollTimer) clearInterval(pollTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
    };
  }, [navigate, refreshAuthState]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
            <div>
              <h1 className="text-base font-semibold text-slate-900">Sign-in failed</h1>
              <p className="mt-2 text-sm text-slate-600">{error}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-6 w-full rounded-lg bg-[#08148C] py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#060f6e]"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="flex flex-col items-center gap-3 text-slate-600">
        <Loader2 className="h-6 w-6 animate-spin text-[#08148C]" aria-hidden="true" />
        <output className="text-sm">
          Completing sign-in…
        </output>
      </div>
    </div>
  );
}
